import {
  Paper,
  Stack,
  Alert,
  AlertTitle,
  Chip,
  Typography,
  useTheme,
} from "@mui/material";
import { CampaignDetails } from "Components/Campaigns/CampaignDetails";
import { DonationForm } from "Components/Campaigns/DonationForm";
import { DonationUnavailableMessage } from "Components/Campaigns/DonationUnavailableMessage";
import { HeroSection } from "Components/Shared/HeroSection";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { getCampaignImages } from "Utils/Campaigns/campaignImageMapping";
import { getCampaignImageUrl } from "Api/Queries/Campaigns/useCampaignImageUrl";
import { CampaignImageType } from "Infrastructure/Api/Api";
import { InfoCards } from "Components/Campaigns/InfoCards";
import { getStatusMessage } from "Components/Campaigns/campaignStatusUtils";
import { formatDate } from "Utils/DateUtils";
import { Resources, useResource } from "Translations/Resources";
import type { CampaignDto } from "Infrastructure/Api/Api";
import {
  calculateCampaignStatus,
  isCampaignActive,
} from "Utils/Campaign/campaignStatusUtils";

const RESOURCES = Resources.Campaigns.Campaign.Page;

type Props = {
  campaign: CampaignDto;
};

export const CampaignPageContent = ({ campaign }: Props) => {
  const theme = useTheme();
  const { t } = useResource();

  const {
    code,
    color,
    currency,
    endDate,
    header,
    description,
    linkUrl,
    linkName,
  } = campaign;
  const campaignStatus = calculateCampaignStatus(campaign);
  const canDonate = isCampaignActive(campaign);
  const campaignImages = getCampaignImages(code);
  const statusMessage = getStatusMessage(campaign, t);

  const apiLandingUrl = getCampaignImageUrl(
    campaign,
    CampaignImageType.Landing,
  );
  const landingImage = apiLandingUrl || campaignImages?.landing;

  const apiIconUrl = getCampaignImageUrl(campaign, CampaignImageType.Icon);

  return (
    <>
      <Paper
        elevation={0}
        sx={{ borderRadius: "10px", overflow: "hidden", mt: 0 }}
      >
        <Stack gap={5} pb={[0, 15]}>
          <HeroSection
            backgroundImage={landingImage}
            {...(apiIconUrl
              ? { iconUrl: apiIconUrl }
              : {
                  icon: (
                    <FavoriteIcon
                      sx={{
                        fontSize: "42px",
                        color: color ?? theme.palette.primary.main,
                      }}
                    />
                  ),
                })}
          />
          <Stack
            p={4}
            gap={5}
            width="100%"
            maxWidth={500}
            alignItems="center"
            alignSelf="center"
            sx={{ boxSizing: "border-box" }}
          >
            {statusMessage && (
              <Alert severity={statusMessage.severity} sx={{ width: "100%" }}>
                <AlertTitle>{statusMessage.title}</AlertTitle>
                <Typography variant="body2">{statusMessage.message}</Typography>
              </Alert>
            )}
            {campaignStatus === "active" && endDate && (
              <Chip
                label={t(RESOURCES.EndsOn, {
                  date: formatDate(endDate),
                })}
                color="info"
                variant="outlined"
                sx={{ alignSelf: "center" }}
              />
            )}
            <CampaignDetails
              header={header}
              description={description}
              linkUrl={linkUrl}
              linkName={linkName}
            />
            {canDonate ? (
              <DonationForm
                campaignCode={code}
                color={color}
                currencyCode={currency}
              />
            ) : (
              <DonationUnavailableMessage />
            )}
          </Stack>
        </Stack>
      </Paper>
      <InfoCards color={color} />
    </>
  );
};
